// alternative-dashboard\mappers\compactSummaryCardsMapper.ts
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import type { BalanceBreakdown } from "@/app/types/accounts";
import type { DashboardBalanceAccountsResponse } from "@/app/types/alterNativeDashboardLayout";
import type { SummaryCardItem } from "../types/summaryCards";
import { gxFormatDateTime } from "@/app/utils/helpers/formatLastUpdated";

const toAmountParts = (amount: number, currency: string) => {
  const formatted = gxCurrencyFormat(amount, currency);

  return {
    amountWhole: formatted.integer,
    amountDecimal: formatted.decimal,
  };
};

// Builds Compact Widget Summary Cards
// Data Source:
// - Total Cash Balance -> gxAccountBalanceTotalApi
// - Assets            -> gxAccountBalanceTotalApi
// - Liabilities       -> gxAccountBalanceTotalApi
// - Net Worth         -> gxDashboardBalanceAccountsApi (netPosition)

export const buildCompactSummaryCards = (
  balanceData: BalanceBreakdown | undefined,
  dashboardBalanceData: DashboardBalanceAccountsResponse | undefined,
  currencyLabel: string,
): SummaryCardItem[] => {
  // gxAccountBalanceTotalApi
  const totalBalance = balanceData?.totalbalance?.totalBalance ?? 0;
  const assetsBalance = balanceData?.totalassetsbalance?.totalBalance ?? 0;
  const liabilitiesBalance =
    balanceData?.totalliablitiesbalance?.totalBalance ?? 0;

  const casa = balanceData?.totalassetsbalance?.casa ?? 0;
  const deposit = balanceData?.totalassetsbalance?.deposit ?? 0;

  const loan = balanceData?.totalliablitiesbalance?.loan ?? 0;
  const card = balanceData?.totalliablitiesbalance?.card ?? 0;

  // gxDashboardBalanceAccountsApi
  const netPosition = dashboardBalanceData?.netPosition ?? 0;

  const totalCashParts = toAmountParts(totalBalance, currencyLabel);
  const assetsParts = toAmountParts(assetsBalance, currencyLabel);
  const liabilitiesParts = toAmountParts(liabilitiesBalance, currencyLabel);
  const netWorthParts = toAmountParts(Math.abs(netPosition), currencyLabel);

  const trendValue = dashboardBalanceData?.momPercentage ?? 0;

  const currencySymbol = gxCurrencyFormat(0, currencyLabel).symbol;

  return [
    {
      id: "total-cash-balance",
      title: "Total Cash Balance",
      titleVariant: "default",
      currency: currencySymbol,
      amountWhole: totalCashParts.amountWhole,
      amountDecimal: totalCashParts.amountDecimal,
      amountTone: "neutral",
      captionTone: "neutral",
      background: "linear-gradient(90deg, #19267A 0%, #3040A3 100%)",
      color: "#FFFFFF",
      variant: "primary",
      lastUpdated: gxFormatDateTime(balanceData?.balanceUpdateOn),
    },
    {
      id: "assets",
      title: "ASSETS",
      titleVariant: "label",
      currency: currencySymbol,
      amountWhole: assetsParts.amountWhole,
      amountDecimal: assetsParts.amountDecimal,
      amountTone: "neutral",
      captionText: `Accts ${casa} · Dep ${deposit}`,
      captionTone: "neutral",
    },
    {
      id: "liabilities",
      title: "LIABILITIES",
      titleVariant: "label",
      currency: currencySymbol,
      amountWhole: liabilitiesParts.amountWhole,
      amountDecimal: liabilitiesParts.amountDecimal,
      amountTone: "neutral",
      captionText: `Loans ${loan} · Cards ${card}`,
      captionTone: "neutral",
    },
    {
      id: "net-worth",
      title: "NET WORTH",
      titleVariant: "label",
      currency: currencySymbol,
      amountWhole:
        netPosition < 0
          ? `-${netWorthParts.amountWhole}`
          : netWorthParts.amountWhole,
      amountDecimal: netWorthParts.amountDecimal,

      amountTone: "neutral",

      trend: {
        label: `${Math.abs(trendValue)} % MoM`,
        direction: trendValue >= 0 ? "up" : "down",
        tone: trendValue >= 0 ? "positive" : "negative",
      },
    },
  ];
};

// // alternative-dashboard\mappers\compactSummaryCardsMapper.ts
// import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
// import type { BalanceBreakdown } from "@/app/types/accounts";
// import type { DashboardBalanceAccountsResponse } from "@/app/types/alterNativeDashboardLayout";
// import type { SummaryCardItem } from "../types/summaryCards";
// import { gxFormatDateTime } from "@/app/utils/helpers/formatLastUpdated";

// const toAmountParts = (amount: number, currency: string) => {
//   const formatted = gxCurrencyFormat(amount, currency);

//   return {
//     amountWhole: formatted.integer,
//     amountDecimal: formatted.decimal,
//   };
// };

// // Builds Compact Widget Summary Cards
// // Data Source:
// // - Total Cash Balance -> gxAccountBalanceTotalApi
// // - Assets            -> gxAccountBalanceTotalApi
// // - Liabilities       -> gxAccountBalanceTotalApi
// // - Net Worth         -> gxDashboardBalanceAccountsApi (netPosition)

// export const buildCompactSummaryCards = (
//   balanceData: BalanceBreakdown | undefined,
//   dashboardBalanceData: DashboardBalanceAccountsResponse | undefined,
//   currencyLabel: string,
// ): SummaryCardItem[] => {
//   // gxAccountBalanceTotalApi
//   const totalBalance = balanceData?.totalbalance?.totalBalance ?? 0;
//   const assetsBalance = balanceData?.totalassetsbalance?.totalBalance ?? 0;
//   const liabilitiesBalance =
//     balanceData?.totalliablitiesbalance?.totalBalance ?? 0;

//   const casa = balanceData?.totalassetsbalance?.casa ?? 0;
//   const deposit = balanceData?.totalassetsbalance?.deposit ?? 0;

//   const loan = balanceData?.totalliablitiesbalance?.loan ?? 0;
//   const card = balanceData?.totalliablitiesbalance?.card ?? 0;

//   // gxDashboardBalanceAccountsApi
//   const netPosition = dashboardBalanceData?.netPosition ?? 0;

//   const totalCashParts = toAmountParts(totalBalance, currencyLabel);
//   const assetsParts = toAmountParts(assetsBalance, currencyLabel);
//   const liabilitiesParts = toAmountParts(liabilitiesBalance, currencyLabel);
//   const netWorthParts = toAmountParts(Math.abs(netPosition), currencyLabel);

//   const trendValue = dashboardBalanceData?.changePercentage ?? 0;

//   const currencySymbol = gxCurrencyFormat(0, currencyLabel).symbol;

//   return [
//     {
//       id: "total-cash-balance",
//       title: "Total Cash Balance",
//       titleVariant: "default",
//       currency: currencySymbol,
//       amountWhole: totalCashParts.amountWhole,
//       amountDecimal: totalCashParts.amountDecimal,
//       amountTone: "neutral",
//       captionTone: "neutral",
//       background: "linear-gradient(90deg, #19267A 0%, #3040A3 100%)",
//       color: "#FFFFFF",
//       variant: "primary",
//       lastUpdated: gxFormatDateTime(balanceData?.balanceUpdateOn),
//     },
//     {
//       id: "assets",
//       title: "ASSETS",
//       titleVariant: "label",
//       currency: currencySymbol,
//       amountWhole: assetsParts.amountWhole,
//       amountDecimal: assetsParts.amountDecimal,
//       amountTone: "neutral",
//       captionText: `Accts ${casa} · Dep ${deposit}`,
//       captionTone: "neutral",
//     },
//     {
//       id: "liabilities",
//       title: "LIABILITIES",
//       titleVariant: "label",
//       currency: currencySymbol,
//       amountWhole: liabilitiesParts.amountWhole,
//       amountDecimal: liabilitiesParts.amountDecimal,
//       amountTone: "neutral",
//       captionText: `Loans ${loan} · Cards ${card}`,
//       captionTone: "neutral",
//     },
//     {
//       id: "net-worth",
//       title: "NET WORTH",
//       titleVariant: "label",
//       currency: currencySymbol,
//       amountWhole:
//         netPosition < 0
//           ? `-${netWorthParts.amountWhole}`
//           : netWorthParts.amountWhole,
//       amountDecimal: netWorthParts.amountDecimal,

//       amountTone: "neutral",

//       trend: {
//         label: `${Math.abs(trendValue)} % MoM`,
//         direction: trendValue >= 0 ? "up" : "down",
//         tone: trendValue >= 0 ? "positive" : "negative",
//       },
//     },
//   ];
// };
